import React, { useState, useEffect } from 'react';
import { useDispatch, connect } from 'react-redux';
import { Container, Typography, Grid, Button, RadioButton, TextInput } from '../../canvasComponents/index';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import '../../css/domain/Services/Services.scss';
import messages from './messages';
import CustomAutocompleteDMTI from '../../FormioComponents/Custom/utils/AutocompleteDMTI/AutocompleteDMTI';
import { searchEnterpriseLocations } from '../../redux/Services/actions';
import { validateEmail } from '../../utils/common/isEmailValid';
import { addressValidation } from '../../services/addressService';
import { getRouteUrl } from '../../FormioComponents/utils';
// Redux
import { enterpriseSelector } from '../../redux/Services/selectors';
import useLeavingPageHandler from '../../hooks/useLeavingPageHandler';

function Enterprise({ onFinalContinue, saveAndContinueLater, updateClaim, policyEmail, gwclaimId, serviceData, policyAddress, enterpriseLocations, lastStep, updateCurrentPage }) {
  const dispatch = useDispatch();
  // Selected branch from radio list
  const [branchSelection, setBranchSelection] = useState(serviceData?.selectedLocation && Object.keys(serviceData.selectedLocation).length ? { value: JSON.stringify(serviceData.selectedLocation), parsedObj: serviceData.selectedLocation } : { value: '', parsedObj: {} });
  // Required branch error
  const [branchRequiredError, setBranchRequiredError] = useState({ isLocationInputEmpty: false, hasBranchSelected: false });
  // Enterprise Email
  const [email, setEmail] = useState({ value: serviceData?.emailAddress || policyEmail, isInvalid: false, isEmpty: false });

  // Enterprise steps
  const [step, setStep] = useState(lastStep || 'selectLocation');
  // Controled address typeahead input field state
  const [addressInput, setAddressInput] = useState(serviceData?.searchedLocation || policyAddress);
  // Selected address object from the address typeahead input field
  const [userLocation, setUserLocation] = useState({});
  // enterprise locations object
  const [enterpriseSearchLocations, setEnterpriseSearchLocations] = useState([]);
  // integration error happened
  const [errorHappened, setErrorHappened] = useState(false);
  // data to save to DB
  const [dbData, setDbData] = useState({ ...serviceData });
  const [isUpdateRequired, setIsUpdateRequired] = useState(false);
  // UseIntl for string translations
  const intl = useIntl();
  const { isLeavingSoSoonPopUpOpen } = useLeavingPageHandler('Vehicle', document.querySelector('.services-heading')?.textContent, true);
  const getDmtiAddress = async (addressStr) => {
    try {
      let finalAddress = {};
      const userAddress = await addressValidation(addressStr);
      if (userAddress.addressValidation?.addressValidationResult === 'true') {
        const { streetNumber, streetName, municipality, province, postalCode } = userAddress.addressValidation;
        finalAddress = {
          addressObj: {
            street: streetNumber,
            streetName,
            city: municipality,
            provinceState: province,
            postalCode,
          },
        };
        return finalAddress;
      }
    } catch (error) {
      console.log('TODO CALL ERROR SERVICE');
    }
  };

  useEffect(() => {
    window.history.pushState({}, window.location.pathname, `#/${getRouteUrl(document.querySelector('.services-heading')?.textContent, 'Vehicle') || ''}`);
  }, [step]);

  useEffect(() => {
    if (branchSelection.value !== '') {
      setBranchRequiredError((prev) => ({ ...prev, hasBranchSelected: false }));
    }
  }, [branchSelection]);

  useEffect(() => {
    if (isLeavingSoSoonPopUpOpen) {
      document.getElementById('leave-popup-button').addEventListener('click', () => {
        saveAndContinueLater('enterprise', step, dbData);
      });
    }
  }, [isLeavingSoSoonPopUpOpen]);
  useEffect(() => {
    if (email.value !== '') {
      const isEmailValid = validateEmail(email.value);
      if (isEmailValid) {
        setEmail((prev) => ({ ...prev, isInvalid: false }));
      }
      if (email.value !== '') {
        setEmail((prev) => ({ ...prev, isEmpty: false }));
      }
    }
  }, [email.value]);

  useEffect(() => {
    if (addressInput === '') {
      setEnterpriseSearchLocations([]);
      setUserLocation({});
      setBranchRequiredError((prev) => ({ ...prev, isLocationInputEmpty: true }));
    } else {
      setBranchRequiredError((prev) => ({ ...prev, isLocationInputEmpty: false }));
    }
    setDbData({ ...dbData, searchedLocation: addressInput });
  }, [addressInput]);

  useEffect(() => {
    setEnterpriseSearchLocations(enterpriseLocations?.searchLocations?.data !== null ? enterpriseLocations.searchLocations.data.map((item) => {
      const { locationDetails } = item.locationInformation;
      return ({
        id: locationDetails?.locationId,
        branch: locationDetails?.locationName,
        address: `${locationDetails.address.street1} ${locationDetails.address.city}, ${locationDetails.address.stateProvince} ${locationDetails.address.postalCode}`,
        telephoneNumber: `${locationDetails.telephoneNumber.number}`,
        addressLink: `https://www.google.com/maps/search/?api=1&query=${locationDetails.address.street1} ${locationDetails.address.city}, ${locationDetails.address.stateProvince} ${locationDetails.address.postalCode}`,
        rentalLocation: {
          locationId: locationDetails?.locationId,
          locationStreetAddress1: locationDetails?.address?.street1,
          locationStreetAddress2: locationDetails?.address?.street2,
          locationCity: locationDetails?.address?.city,
          locationCountryCode: locationDetails?.address?.countryCode,
          locationCounty: locationDetails?.address?.county,
          locationLatitude: locationDetails?.geographicCoordinates?.latitude,
          locationLongitude: locationDetails?.geographicCoordinates?.longitude,
          locationPhoneNum: locationDetails?.telephoneNumber?.number,
          locationPostalCode: locationDetails?.address?.postalCode,
          locationState: locationDetails?.address?.stateProvince,
        },
      });
    }) : []);
  }, [enterpriseLocations.searchLocations.data]);

  useEffect(() => {
    if (enterpriseLocations.searchLocations?.error?.error) {
      setErrorHappened(true);
    } else if (errorHappened) {
      onFinalContinue({
        isEnterpriseVisible: true,
        triggerEnterprise: false,
        branch: <FormattedMessage id="Claims Professional to help arrange" defaultMessage="Claims team member to help arrange" />,
      });
      setDbData({ ...dbData, isEnterpriseVisible: true, triggerEnterprise: false });
    }
  }, [enterpriseLocations.searchLocations.error]);

  const handleOnChangeGetLocations = (searchedAddress) => {
    if (searchedAddress.addressObj) {
      const { street, streetName, city, postalCode, provinceState } = searchedAddress.addressObj;
      const strAddress = `${street ? `${street} ` : ''}${streetName ? `${streetName} ` : ''}${city ? `${city} ` : ''}${provinceState ? `${provinceState} ` : ''}${postalCode || ''}`;
      setAddressInput(strAddress);
      setBranchRequiredError((prev) => ({ ...prev, hasBranchSelected: false }));
      const searchCriteria = {
        address: `${street} ${streetName}`,
        city,
        countryCode: 'CA',
        postalCode,
        stateProvince: provinceState,
        geographicCoordinates: {
          latitude: '0',
          longitude: '0',
        },
      };
      dispatch(searchEnterpriseLocations(searchCriteria));
      setUserLocation((prev) => ({ ...prev, searchCriteria }));
    }
  };

  const handleOnChangeDMTI = (searchedAddress) => {
    if (searchedAddress.value !== '' && Object.keys(searchedAddress.addressObj).length > 0) {
      handleOnChangeGetLocations(searchedAddress);
    } else {
      setAddressInput('');
    }
  };

  const onClickContinue = (nextPage) => {
    switch (nextPage) {
      case 'requestEmail':
        if (!userLocation.searchCriteria) {
          setBranchRequiredError((prev) => ({ ...prev, isLocationInputEmpty: true }));
          return;
        }

        if (enterpriseSearchLocations.length > 0 && branchSelection.value === '') {
          setBranchRequiredError((prev) => ({ ...prev, hasBranchSelected: true }));
          return;
        }
        setBranchRequiredError((prev) => ({ ...prev, hasBranchSelected: false }));

        if (Object.keys(userLocation).length <= 0 || enterpriseSearchLocations.length <= 0) {
          onClickContinue('servicesReview');
          return;
        }
        break;
      case 'review':
        const isEmailValid = validateEmail(email.value);
        if (!isEmailValid) {
          setEmail((prev) => ({ ...prev, isInvalid: true }));
          return;
        }
        if (email.value === '' && !isEmailValid) {
          setEmail((prev) => ({ ...prev, isEmpty: true, isInvalid: false }));
          return;
        }
        break;
      case 'servicesReview':
        const rentalLocationObj = branchSelection.parsedObj?.rentalLocation;
        setDbData({ ...dbData, isEnterpriseVisible: false, triggerEnterprise: !!branchSelection.parsedObj?.branch });
        setIsUpdateRequired(true);
        onFinalContinue({
          isEnterpriseVisible: true,
          triggerEnterprise: !!branchSelection.parsedObj?.branch,
          branch: branchSelection.parsedObj?.branch || <FormattedMessage id="Claims Professional to help arrange" defaultMessage="Claims team member to help arrange" />,
          address: branchSelection.parsedObj?.address,
          gwclaimId,
          rentalLocationObj,
          email: email.value,
          previousStep: () => setStep('selectLocation'),
        });
        return;
      default:
        break;
    }
    setIsUpdateRequired(true);
    setStep(nextPage);
  };

  useEffect(() => {
    if (isUpdateRequired) {
      updateClaim('enterprise', step, dbData);
      setIsUpdateRequired(false);
    }
  }, [isUpdateRequired]);

  const fetchAddress = async (address) => {
    const dmtiAddress = await getDmtiAddress(address);
    return dmtiAddress;
  };

  useEffect(() => {
    // call the function
    fetchAddress(addressInput).then((result) => { if (result && result.addressObj) { handleOnChangeGetLocations(result); } }).catch((err) => console.log('TODO CALL ERROR SERVICE', err));
  }, []);

  useEffect(() => {
    switch (step) {
      case 'selectLocation':
        updateCurrentPage(messages.rental, 2);
        break;
      case 'requestEmail':
        updateCurrentPage(messages.schedule, 3);
        break;
      case 'review':
        updateCurrentPage(messages.reviewHeading, 4);
        break;
      default:
        updateCurrentPage(messages.rental, 2);
    }
    // Scroll to the page top
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }, [step]);

  return (
    <>
      {/* Select rental location */}
      {
        step === 'selectLocation' && (
          <Container fluid className="dashboard-content">
            <Grid>
              <div className="align-left">
                <Grid.Row>
                  <Typography h1 center className="services-heading">
                    <FormattedMessage {...messages.rental} />
                  </Typography>
                </Grid.Row>
                <div className="services-divider">
                  <Grid.Row justifyContentCenter flex>
                    <div className="services-copy">
                      <strong className="services-copy subtitle mb-8">
                        <FormattedMessage {...messages.howItWorks} />
                      </strong>
                      <ul className="services-list">
                        <li><FormattedMessage {...messages.list1} /></li>
                        <li><FormattedMessage {...messages.list2} /></li>
                        <li><FormattedMessage {...messages.list3} /></li>
                      </ul>
                      <p><strong><FormattedMessage {...messages.headsup} /></strong> <FormattedMessage {...messages.headsupSubtext} /></p>
                    </div>
                  </Grid.Row>
                  <Grid.Row>
                    <div className="text-input-width">
                      <CustomAutocompleteDMTI
                        index={0}
                        name="address"
                        label={intl.formatMessage({ ...messages.locationNearMe })}
                        id="location_typeahead"
                        placeholder={intl.formatMessage({ ...messages.addressPlaceholder })}
                        value={{ value: addressInput, addressObj: {} }}
                        customOnChange={(value) => {
                          setAddressInput(value);
                          setEnterpriseSearchLocations([]);
                          setUserLocation({});
                          setBranchSelection({ value: '', parsedObj: {} });
                        }}
                        handleChange={(searchedAddress) => handleOnChangeDMTI(searchedAddress)}
                      />
                    </div>
                    {branchRequiredError.isLocationInputEmpty && <p className="error-message"> <FormattedMessage {...messages.requiredErrorMessage} /></p>}
                  </Grid.Row>
                </div>

                {Object.keys(userLocation).length > 0 && enterpriseSearchLocations.length > 0 && (
                  <div className="services-divider">
                    <Grid.Row justifyContentCenter flex className="services-copy">
                      <strong className="mb-18"><FormattedMessage {...messages.closestLocation} /></strong>
                      {enterpriseSearchLocations.map((item) => (
                        <>
                          <RadioButton
                            secondary
                            id={`id_enterprise-branch-${Math.random() * 0xffffff}`}
                            value={JSON.stringify(item)}
                            name="enterprise-branch"
                            onChange={(value) => {
                              setBranchSelection({ value, parsedObj: JSON.parse(value) });
                              setDbData({ ...dbData, selectedLocation: JSON.parse(value) });
                            }}
                            checked={branchSelection.parsedObj?.id === item.id}
                            className="services-radio"
                            key={Math.random() * 0xffffff}
                          >
                            <span className="radio-text">{item.branch}</span>
                          </RadioButton>

                          <p className="address-text">{item.address}</p>
                        </>
                      ))}
                      {branchRequiredError.hasBranchSelected && <p className="error-message"> <FormattedMessage {...messages.requiredErrorMessage} /></p>}
                    </Grid.Row>
                  </div>
                )}
                {Object.keys(userLocation).length > 0 && enterpriseSearchLocations.length <= 0 && (
                  <div className="services-divider mb-30">
                    <Grid.Row justifyContentCenter flex className="services-copy">
                      <strong className="mb-18"><FormattedMessage {...messages.closestLocation} /></strong>
                      <div>
                        <FormattedMessage {...messages.noLocationsError1} />
                        <a style={{ textDecoration: 'none' }} href={`tel:${intl.formatMessage(messages.noLocationsError2)}`}><FormattedMessage {...messages.noLocationsError2} /></a>
                        <FormattedMessage {...messages.noLocationsError3} />
                      </div>
                    </Grid.Row>
                  </div>
                )}

                <Grid.Row justifyContentCenter flex>
                  <div className="center-v services-btns">
                    <Button
                      primary
                      className="mb-20 services-continue-button"
                      onClick={() => onClickContinue('requestEmail')}
                    ><FormattedMessage {...messages.continueCta} />
                    </Button>
                    <Button
                      className="btn save-continue-later"
                      onClick={() => saveAndContinueLater('enterprise', step, dbData)}
                    >
                      <FormattedMessage {...messages.saveContinueCta} />
                    </Button>
                  </div>
                </Grid.Row>
              </div>
            </Grid>
          </Container>
        )
      }
      {/* Enter your email */}
      {
        step === 'requestEmail' && (
          <Container fluid className="dashboard-content">
            <Grid>
              <Grid.Row>
                <Typography h1 center className="services-heading">
                  <FormattedMessage {...messages.schedule} />
                </Typography>
              </Grid.Row>
              <div className="services-divider">
                <Grid.Row justifyContentCenter flex>
                  <div>
                    <p className="services-copy">
                      <strong className="services-copy subtitle">
                        <FormattedMessage {...messages.reservationTitle} />
                      </strong>
                    </p>

                    <p className="services-copy">
                      <strong className="services-small-fonts">{branchSelection.parsedObj?.branch}</strong>
                      <br />
                      <span className="services-small-fonts">{branchSelection.parsedObj?.address}</span> <br />
                      <a href={`tel: ${branchSelection.parsedObj?.telephoneNumber}`} className="services-tertiary-button">{branchSelection.parsedObj?.telephoneNumber?.replace(/^(\d{3})(\d{3})(\d{4}).*/, '($1) $2-$3')}</a> <br />
                      <Button primary underline link href={branchSelection.parsedObj?.addressLink} className="view-map-button" newTab style={{ color: '#147582' }}>
                        <FormattedMessage {...messages.viewMap} />
                      </Button>
                    </p>
                  </div>
                </Grid.Row>
              </div>

              <div className="services-divider">
                <Grid.Row>
                  <div className="text-input-width fs-exclude">
                    <TextInput
                      label={intl.formatMessage({ id: messages.emailAddress.defaultMessage, defaultValue: messages.emailAddress.defaultMessage })}
                      placeholder={intl.formatMessage({ id: messages.emailPlaceholder.id, defaultValue: messages.emailPlaceholder.defaultMessage })}
                      defaultValue={email.value}
                      onChange={(value) => {
                        setEmail((prev) => ({ ...prev, value }));
                        setDbData({ ...dbData, emailAddress: value });
                      }}
                    />
                  </div>
                  {email.isInvalid && <p className="error-message"> <FormattedMessage {...messages.emailAddressErrorMessage} /></p>}
                  {email.isEmpty && <p className="error-message"> <FormattedMessage {...messages.requiredErrorMessage} /></p>}
                </Grid.Row>
              </div>

              <Grid.Row justifyContentCenter flex>
                <div className="center-v services-btns">
                  <Button
                    primary
                    className="mb-20 services-continue-button"
                    onClick={() => onClickContinue('review')}
                  ><FormattedMessage {...messages.continueCta} />
                  </Button>
                  <Button
                    secondary
                    className="mb-20 services-continue-button"
                    onClick={() => setStep('selectLocation')}
                  ><FormattedMessage {...messages.pickLocationCta} />
                  </Button>
                  <Button
                    className="btn save-continue-later"
                    onClick={() => saveAndContinueLater('enterprise', step, dbData)}
                  >
                    <FormattedMessage {...messages.saveContinueCta} />
                  </Button>
                </div>
              </Grid.Row>
            </Grid>
          </Container>
        )
      }
      {/* Review */}
      {
        step === 'review'
        && (
          <Container fluid className="dashboard-content">
            <Grid>
              <Grid.Row>
                <Typography h1 center className="services-heading">
                  <FormattedMessage {...messages.reviewHeading} />
                </Typography>
              </Grid.Row>
              <div className="services-divider">
                <Grid.Row justifyContentCenter flex>
                  <div className="services-copy">
                    <p>
                      <FormattedMessage {...messages.reviewIntroFirstPart} />
                      <strong> <FormattedMessage {...messages.reviewIntroContinue} /> </strong>
                      <FormattedMessage {...messages.reviewIntroSecondPart} />
                    </p>
                    <p className="services-subhead">
                      <FormattedMessage {...messages.reviewTitle} />
                    </p>
                  </div>

                  <p className="services-copy">
                    <strong className="services-small-fonts">{branchSelection.parsedObj?.branch}</strong>
                    <br />
                    <span className="services-small-fonts">{branchSelection.parsedObj?.address}</span> <br />
                    <a href={`tel: ${branchSelection.parsedObj?.telephoneNumber}`} className="services-tertiary-button">{branchSelection.parsedObj?.telephoneNumber?.replace(/^(\d{3})(\d{3})(\d{4}).*/, '($1) $2-$3')}</a>
                  </p>

                  <div className="services-copy">
                    <p className="mb-0">
                      <strong className="subtitle">
                        <FormattedMessage {...messages.reviewNextSteps} />
                      </strong> <br />
                      <FormattedMessage {...messages.reviewListInto} /> <br />
                      <FormattedMessage {...messages.reviewListInto1} />
                    </p>
                    <ul className="services-list">
                      <li><FormattedMessage {...messages.reviewList1} /></li>
                      <li><FormattedMessage {...messages.reviewList2} /></li>
                      <li><FormattedMessage {...messages.reviewList3} /></li>
                      <li><FormattedMessage {...messages.reviewList4} /></li>
                      <li><FormattedMessage {...messages.reviewList5} /></li>
                    </ul>
                  </div>
                </Grid.Row>
              </div>

              <Grid.Row justifyContentCenter flex>
                <div className="center-v services-btns">
                  <Button
                    primary
                    className="mb-20 services-continue-button"
                    onClick={() => onClickContinue('servicesReview')}
                  ><FormattedMessage {...messages.continueCta} />
                  </Button>
                  <Button
                    className="btn save-continue-later"
                    onClick={() => saveAndContinueLater('enterprise', step, dbData)}
                  >
                    <FormattedMessage {...messages.saveContinueCta} />
                  </Button>
                </div>
              </Grid.Row>
            </Grid>
          </Container>
        )
      }
    </>
  );
}

const mapStateToProps = (state) => ({
  enterpriseLocations: enterpriseSelector(state.services),
});

export default injectIntl(connect(mapStateToProps)(Enterprise));
